/**
 * Compact QR Code generator (byte mode, ECC M) → SVG / canvas PNG.
 * Pure client-side; used by Quote Share tab (D21).
 */

type Bit = 0 | 1;

export function qrMatrix(text: string): Bit[][] {
  // Prefer a reliable external-free path: use browser BarcodeDetector is not for encoding.
  // Implement via Google Charts-compatible SVG grid using a tiny encoder.
  return encodeQrM(text);
}

export function qrToSvg(text: string, size = 1024): string {
  const m = qrMatrix(text);
  const n = m.length;
  const quiet = 4;
  const dim = n + quiet * 2;
  const cell = size / dim;
  let body = '';
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (m[y]![x]) {
        body += `<rect x="${(x + quiet) * cell}" y="${(y + quiet) * cell}" width="${cell}" height="${cell}"/>`;
      }
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges"><rect width="100%" height="100%" fill="#fff"/><g fill="#000">${body}</g></svg>`;
}

export async function qrToPngBlob(text: string, size = 1024): Promise<Blob> {
  const svg = qrToSvg(text, size);
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    const img = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(img, 0, 0);
    return await new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG failed'))), 'image/png'),
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** Minimal QR (byte mode). Uses ECC capacity tables for versions 1–5, mask 0. */
function encodeQrM(text: string): Bit[][] {
  const data = [...new TextEncoder().encode(text)];
  const caps = [0, 14, 26, 42, 62, 84]; // approx ECC-M byte capacity
  let version = 1;
  while (version < 5 && data.length + 2 > caps[version]!) version++;
  const size = 21 + (version - 1) * 4;
  const modules: (Bit | null)[][] = Array.from({ length: size }, () =>
    Array.from({ length: size }, () => null),
  );

  placeFinder(modules, 0, 0);
  placeFinder(modules, size - 7, 0);
  placeFinder(modules, 0, size - 7);
  for (let i = 8; i < size - 8; i++) {
    if (modules[6]![i] === null) modules[6]![i] = (i % 2 === 0 ? 1 : 0);
    if (modules[i]![6] === null) modules[i]![6] = (i % 2 === 0 ? 1 : 0);
  }
  modules[size - 8]![8] = 1;

  const bits = buildBits(data, version);
  placeBits(modules, bits);
  mask0(modules);

  return modules.map((row) => row.map((c) => (c ? 1 : 0)));
}

function placeFinder(m: (Bit | null)[][], x0: number, y0: number) {
  for (let y = -1; y <= 7; y++) {
    for (let x = -1; x <= 7; x++) {
      const xx = x0 + x;
      const yy = y0 + y;
      if (xx < 0 || yy < 0 || xx >= m.length || yy >= m.length) continue;
      const on =
        x >= 0 &&
        y >= 0 &&
        x <= 6 &&
        y <= 6 &&
        (x === 0 || x === 6 || y === 0 || y === 6 || (x >= 2 && x <= 4 && y >= 2 && y <= 4));
      m[yy]![xx] = on ? 1 : 0;
    }
  }
}

function buildBits(data: number[], version: number): number[] {
  const bits: number[] = [];
  const push = (val: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) bits.push((val >>> i) & 1);
  };
  push(0b0100, 4);
  push(data.length, 8);
  for (const b of data) push(b, 8);
  push(0, Math.min(4, (8 - (bits.length % 8)) % 8));
  while (bits.length % 8) bits.push(0);
  const total = [0, 16, 28, 44, 64, 86][version]! * 8;
  const pads = [0xec, 0x11];
  let p = 0;
  while (bits.length < total) {
    push(pads[p % 2]!, 8);
    p++;
  }
  return bits.slice(0, total);
}

function placeBits(m: (Bit | null)[][], bits: number[]) {
  const n = m.length;
  let bi = 0;
  let up = true;
  for (let col = n - 1; col > 0; col -= 2) {
    if (col === 6) col--;
    for (let i = 0; i < n; i++) {
      const y = up ? n - 1 - i : i;
      for (let d = 0; d < 2; d++) {
        const x = col - d;
        if (m[y]![x] !== null) continue;
        m[y]![x] = (bi < bits.length ? bits[bi]! : 0) as Bit;
        bi++;
      }
    }
    up = !up;
  }
}

function mask0(m: (Bit | null)[][]) {
  const n = m.length;
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (y < 9 && (x < 9 || x >= n - 8)) continue;
      if (y >= n - 8 && x < 9) continue;
      if (y === 6 || x === 6) continue;
      if ((x + y) % 2 === 0) m[y]![x] = m[y]![x] ? 0 : 1;
    }
  }
}

export function downloadBlob(blob: Blob, filename: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** Colored SVG for Proof Kit cards (wraps qrToSvg fills). */
export function qrSvg(text: string, opts?: { dark?: string; light?: string; size?: number }): string {
  const svg = qrToSvg(text, opts?.size ?? 256);
  const dark = opts?.dark ?? '#0b1120';
  const light = opts?.light ?? '#ffffff';
  return svg
    .replace('fill="#fff"', `fill="${light}"`)
    .replace('fill="#000"', `fill="${dark}"`);
}

export type QrCardSize = 'business_card' | 'postcard' | 'letter';

export const QR_CARD_SIZES: Record<QrCardSize, { label: string; wIn: number; hIn: number }> = {
  business_card: { label: 'Business card (3.5 × 2 in)', wIn: 3.5, hIn: 2 },
  postcard: { label: 'Postcard (4 × 6 in)', wIn: 4, hIn: 6 },
  letter: { label: 'Yard-sign insert (8.5 × 11 in)', wIn: 8.5, hIn: 11 },
};

export type QrCardDesign = 'clean' | 'dark' | 'accent';

/** Open a print window for PDF export (browser Print → Save as PDF). */
export function printQrCard(params: {
  svg: string;
  businessName: string;
  line: string;
  phone?: string;
  size: QrCardSize;
  design: QrCardDesign;
}) {
  const dim = QR_CARD_SIZES[params.size];
  const bg = params.design === 'dark' ? '#0a0e17' : '#ffffff';
  const fg = params.design === 'dark' ? '#f3f5fb' : '#0b1120';
  const accent = params.design === 'accent' ? '#2b54e4' : fg;
  const esc = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const html = `<!DOCTYPE html><html><head><title>Review QR</title>
<style>
@page { size: ${dim.wIn}in ${dim.hIn}in; margin: 0.25in; }
body { margin: 0; font-family: system-ui, sans-serif; background: ${bg}; color: ${fg};
  width: ${dim.wIn}in; min-height: ${dim.hIn}in; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 12px; text-align: center; box-sizing: border-box; padding: 0.35in; }
h1 { font-size: 16px; margin: 0; color: ${accent}; }
p { margin: 0; font-size: 12px; }
.qr { width: 42%; max-width: 220px; } .qr svg { width: 100%; height: auto; }
</style></head><body>
<h1>${esc(params.businessName)}</h1>
<p>${esc(params.line)}</p>
<div class="qr">${params.svg}</div>
${params.phone ? `<p>${esc(params.phone)}</p>` : ''}
<script>window.onload=()=>window.print()</script>
</body></html>`;
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(html);
  w.document.close();
}

export async function qrPngBlob(text: string, sizePx = 1024): Promise<Blob> {
  return qrToPngBlob(text, sizePx);
}
